import { lngLatToMercatorMeters, TILE_SIZE_METERS, VISUAL_REVEAL_RADIUS_METERS } from '@repo/map-preferences/fog-tiles';
import type * as MapLibre from 'maplibre-gl';
import { BG_FRAGMENT_SRC, BG_VERTEX_SRC, COMP_FRAGMENT_SRC, ERASE_FRAGMENT_SRC } from './fog-of-war-shaders';

export const FOG_LAYER_ID = 'fog-of-war';

function isWebGL2(gl: WebGLRenderingContext | WebGL2RenderingContext): gl is WebGL2RenderingContext {
    return typeof WebGL2RenderingContext !== 'undefined' && gl instanceof WebGL2RenderingContext;
}

function resetGlState(gl: WebGLRenderingContext | WebGL2RenderingContext): void {
    if (isWebGL2(gl)) gl.bindVertexArray(null);
    gl.disable(gl.DEPTH_TEST);
    gl.depthMask(false);
    gl.disable(gl.STENCIL_TEST);
    gl.disable(gl.CULL_FACE);
    gl.disable(gl.SCISSOR_TEST);
    gl.colorMask(true, true, true, true);
    gl.blendEquation(gl.FUNC_ADD);
}

interface FogOfWarLayerConfig {
    getCircles: () => Array<[number, number]>;
    getEnabled: () => boolean;
    getShouldRunWhenUnfocused?: () => boolean;
    getMapBgColor?: () => [number, number, number] | null;
}

interface GlState {
    bgProgram: WebGLProgram;
    eraseProgram: WebGLProgram;
    compositeProgram: WebGLProgram;
    bgAPositionLoc: number;
    bgUTimeLoc: WebGLUniformLocation | null;
    bgUZoomLoc: WebGLUniformLocation | null;
    bgUWorldBoundsLoc: WebGLUniformLocation | null;
    bgUMapTintLoc: WebGLUniformLocation | null;
    eraseAPositionLoc: number;
    eraseUAtlasLoc: WebGLUniformLocation | null;
    eraseUWorldBoundsLoc: WebGLUniformLocation | null;
    eraseUAtlasOriginLoc: WebGLUniformLocation | null;
    eraseUTileSizeLoc: WebGLUniformLocation | null;
    compAPositionLoc: number;
    compUTexLoc: WebGLUniformLocation | null;
    fullscreenVbo: WebGLBuffer;
    maxTextureSize: number;
    // 1px-per-tile visibility grid covering the data bounding box
    gridTexture: WebGLTexture;
    gridMinTx: number;
    gridMinTy: number;
    gridW: number;
    gridH: number;
    gridDirty: boolean;
    // Dirty row range for partial texture uploads (inclusive)
    dirtyRowMin: number;
    dirtyRowMax: number;
    fbo: WebGLFramebuffer;
    fboTexture: WebGLTexture;
    fboWidth: number;
    fboHeight: number;
}

export class FogOfWarLayer implements MapLibre.CustomLayerInterface {
    readonly id = FOG_LAYER_ID;
    readonly type = 'custom';
    readonly renderingMode = '2d';
    private map: MapLibre.Map | null = null;
    private state: GlState | null = null;
    private animationIntervalId: ReturnType<typeof setInterval> | null = null;
    private animationTimeMs = 0;
    private lastAnimationTimestampMs = 0;
    private windowFocused = true;
    private lastCircleCount = 0;
    private revealBuf: Uint8Array | null = null;
    private revealMinTx = 0;
    private revealMinTy = 0;
    private revealW = 0;
    private revealH = 0;
    private bboxTxMin = Number.POSITIVE_INFINITY;
    private bboxTxMax = Number.NEGATIVE_INFINITY;
    private bboxTyMin = Number.POSITIVE_INFINITY;
    private bboxTyMax = Number.NEGATIVE_INFINITY;
    private handleFocusStateChange = (event?: Event): void => {
        const wasRunning = this.animationIntervalId !== null;
        if (event?.type === 'blur' || event?.type === 'pagehide') {
            this.windowFocused = false;
        } else if (event?.type === 'focus' || event?.type === 'pageshow') {
            this.windowFocused = true;
        } else {
            this.windowFocused = this.getWindowFocused();
        }
        if (this.shouldRunIdleAnimation()) {
            if (!wasRunning) this.lastAnimationTimestampMs = performance.now();
            this.startAnimationInterval();
            this.map?.triggerRepaint();
        } else {
            this.stopAnimationInterval();
        }
    };

    constructor(private readonly config: FogOfWarLayerConfig) {}

    onAdd(map: MapLibre.Map, gl: WebGLRenderingContext | WebGL2RenderingContext): void {
        this.map = map;
        this.lastAnimationTimestampMs = performance.now();
        this.windowFocused = this.getWindowFocused();
        const bgProgram = compileProgram(gl, BG_VERTEX_SRC, BG_FRAGMENT_SRC);
        const eraseProgram = compileProgram(gl, BG_VERTEX_SRC, ERASE_FRAGMENT_SRC);
        const compositeProgram = compileProgram(gl, BG_VERTEX_SRC, COMP_FRAGMENT_SRC);

        const fullscreenVbo = gl.createBuffer();
        if (!fullscreenVbo) throw new Error('FogOfWarLayer: createBuffer null');
        gl.bindBuffer(gl.ARRAY_BUFFER, fullscreenVbo);
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);

        const fbo = gl.createFramebuffer();
        if (!fbo) throw new Error('FogOfWarLayer: createFramebuffer null');
        const fboTexture = gl.createTexture();
        if (!fboTexture) throw new Error('FogOfWarLayer: createTexture null');
        const gridTexture = gl.createTexture();
        if (!gridTexture) throw new Error('FogOfWarLayer: createTexture null');

        this.state = {
            bgProgram,
            eraseProgram,
            compositeProgram,
            bgAPositionLoc: gl.getAttribLocation(bgProgram, 'a_position'),
            bgUTimeLoc: gl.getUniformLocation(bgProgram, 'u_time'),
            bgUZoomLoc: gl.getUniformLocation(bgProgram, 'u_zoom'),
            bgUWorldBoundsLoc: gl.getUniformLocation(bgProgram, 'u_worldBounds'),
            bgUMapTintLoc: gl.getUniformLocation(bgProgram, 'u_mapTint'),
            eraseAPositionLoc: gl.getAttribLocation(eraseProgram, 'a_position'),
            eraseUAtlasLoc: gl.getUniformLocation(eraseProgram, 'u_atlas'),
            eraseUWorldBoundsLoc: gl.getUniformLocation(eraseProgram, 'u_worldBounds'),
            eraseUAtlasOriginLoc: gl.getUniformLocation(eraseProgram, 'u_atlasOrigin'),
            eraseUTileSizeLoc: gl.getUniformLocation(eraseProgram, 'u_tileSize'),
            compAPositionLoc: gl.getAttribLocation(compositeProgram, 'a_position'),
            compUTexLoc: gl.getUniformLocation(compositeProgram, 'u_tex'),
            fullscreenVbo,
            maxTextureSize: gl.getParameter(gl.MAX_TEXTURE_SIZE) as number,
            gridTexture,
            gridMinTx: 0,
            gridMinTy: 0,
            gridW: 0,
            gridH: 0,
            gridDirty: true,
            dirtyRowMin: 0,
            dirtyRowMax: 0,
            fbo,
            fboTexture,
            fboWidth: 0,
            fboHeight: 0,
        };
        this.addFocusListeners();
        this.handleFocusStateChange();
    }

    render(gl: WebGLRenderingContext | WebGL2RenderingContext, _args: MapLibre.CustomRenderMethodInput): void {
        if (!this.map || !this.state || !this.config.getEnabled()) return;
        resetGlState(gl);
        const s = this.state;
        const circles = this.config.getCircles();
        const canvasW = this.map.getCanvas().width;
        const canvasH = this.map.getCanvas().height;
        // Half-res FBO cuts fill-rate ~4x; the soft noise effect tolerates it.
        const w = Math.max(1, canvasW >> 1);
        const h = Math.max(1, canvasH >> 1);

        gl.activeTexture(gl.TEXTURE0);
        gl.bindTexture(gl.TEXTURE_2D, null);
        this.ensureFbo(gl, w, h);

        gl.bindFramebuffer(gl.FRAMEBUFFER, s.fbo);
        gl.viewport(0, 0, w, h);
        gl.clearColor(0, 0, 0, 0);
        gl.clear(gl.COLOR_BUFFER_BIT);
        // biome-ignore lint/correctness/useHookAtTopLevel: WebGL API
        gl.useProgram(s.bgProgram);
        gl.bindBuffer(gl.ARRAY_BUFFER, s.fullscreenVbo);
        gl.enableVertexAttribArray(s.bgAPositionLoc);
        gl.vertexAttribPointer(s.bgAPositionLoc, 2, gl.FLOAT, false, 0, 0);
        if (s.bgUTimeLoc) gl.uniform1f(s.bgUTimeLoc, this.getAnimationTimeSeconds());
        if (s.bgUZoomLoc) gl.uniform1f(s.bgUZoomLoc, this.map.getZoom());
        const bounds = this.map.getBounds();
        const [minX, minY] = lngLatToMercatorMeters(bounds.getWest(), bounds.getSouth());
        const [maxX, maxY] = lngLatToMercatorMeters(bounds.getEast(), bounds.getNorth());
        if (s.bgUWorldBoundsLoc) gl.uniform4f(s.bgUWorldBoundsLoc, minX, minY, maxX, maxY);
        if (s.bgUMapTintLoc) {
            const tint = this.config.getMapBgColor?.();
            if (tint) gl.uniform4f(s.bgUMapTintLoc, tint[0], tint[1], tint[2], 1.0);
            else gl.uniform4f(s.bgUMapTintLoc, 0, 0, 0, 0);
        }
        gl.disable(gl.BLEND);
        gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);

        this.updateGrid(gl, circles);
        if (s.gridW > 0 && s.gridH > 0) {
            // biome-ignore lint/correctness/useHookAtTopLevel: WebGL API
            gl.useProgram(s.eraseProgram);
            gl.bindBuffer(gl.ARRAY_BUFFER, s.fullscreenVbo);
            gl.enableVertexAttribArray(s.eraseAPositionLoc);
            gl.vertexAttribPointer(s.eraseAPositionLoc, 2, gl.FLOAT, false, 0, 0);
            if (s.eraseUAtlasLoc) gl.uniform1i(s.eraseUAtlasLoc, 0);
            if (s.eraseUWorldBoundsLoc) gl.uniform4f(s.eraseUWorldBoundsLoc, minX, minY, maxX, maxY);
            if (s.eraseUAtlasOriginLoc)
                gl.uniform4f(s.eraseUAtlasOriginLoc, s.gridMinTx, s.gridMinTy, s.gridW, s.gridH);
            if (s.eraseUTileSizeLoc) gl.uniform1f(s.eraseUTileSizeLoc, TILE_SIZE_METERS);
            gl.activeTexture(gl.TEXTURE0);
            gl.bindTexture(gl.TEXTURE_2D, s.gridTexture);
            gl.enable(gl.BLEND);
            gl.blendFuncSeparate(gl.ZERO, gl.ONE_MINUS_SRC_ALPHA, gl.ZERO, gl.ONE_MINUS_SRC_ALPHA);
            gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
        }

        // Composite the half-res FBO back at full canvas resolution.
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
        gl.viewport(0, 0, canvasW, canvasH);
        // biome-ignore lint/correctness/useHookAtTopLevel: WebGL API
        gl.useProgram(s.compositeProgram);
        gl.bindBuffer(gl.ARRAY_BUFFER, s.fullscreenVbo);
        gl.enableVertexAttribArray(s.compAPositionLoc);
        gl.vertexAttribPointer(s.compAPositionLoc, 2, gl.FLOAT, false, 0, 0);
        if (s.compUTexLoc) gl.uniform1i(s.compUTexLoc, 0);
        gl.activeTexture(gl.TEXTURE0);
        gl.bindTexture(gl.TEXTURE_2D, s.fboTexture);
        gl.enable(gl.BLEND);
        gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
        gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
        gl.disable(gl.BLEND);
        gl.bindTexture(gl.TEXTURE_2D, null);
    }

    onRemove(_map: MapLibre.Map, gl: WebGLRenderingContext | WebGL2RenderingContext): void {
        this.removeFocusListeners();
        this.stopAnimationInterval();
        if (!this.state) return;
        const s = this.state;
        gl.deleteProgram(s.bgProgram);
        gl.deleteProgram(s.eraseProgram);
        gl.deleteProgram(s.compositeProgram);
        gl.deleteBuffer(s.fullscreenVbo);
        gl.deleteTexture(s.gridTexture);
        gl.deleteFramebuffer(s.fbo);
        gl.deleteTexture(s.fboTexture);
        this.state = null;
        this.map = null;
    }

    /** Reset the grid so the next render rebuilds from all circles. Called on hydrate/clear. */
    resetGrid(): void {
        this.lastCircleCount = 0;
        if (this.revealBuf) this.revealBuf.fill(0);
        this.revealW = 0;
        this.revealH = 0;
        this.bboxTxMin = Number.POSITIVE_INFINITY;
        this.bboxTxMax = Number.NEGATIVE_INFINITY;
        this.bboxTyMin = Number.POSITIVE_INFINITY;
        this.bboxTyMax = Number.NEGATIVE_INFINITY;
        if (this.state) {
            this.state.gridDirty = true;
            this.state.dirtyRowMin = 0;
            this.state.dirtyRowMax = 0;
            this.state.gridW = 0;
        }
    }

    syncAnimationState(): void {
        this.handleFocusStateChange();
    }

    private ensureFbo(gl: WebGLRenderingContext | WebGL2RenderingContext, w: number, h: number): void {
        if (!this.state || (this.state.fboWidth === w && this.state.fboHeight === h)) return;
        gl.bindTexture(gl.TEXTURE_2D, this.state.fboTexture);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, w, h, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        gl.bindFramebuffer(gl.FRAMEBUFFER, this.state.fbo);
        gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, this.state.fboTexture, 0);
        this.state.fboWidth = w;
        this.state.fboHeight = h;
    }

    private shouldRunIdleAnimation(): boolean {
        if (this.config.getShouldRunWhenUnfocused?.()) return true;
        if (typeof document === 'undefined') return true;
        if (document.hidden) return false;
        if (!this.windowFocused) return false;
        return true;
    }

    private getWindowFocused(): boolean {
        if (typeof document !== 'undefined' && typeof document.hasFocus === 'function') return document.hasFocus();
        return true;
    }

    private getAnimationTimeSeconds(): number {
        const now = performance.now();
        if (this.shouldRunIdleAnimation()) {
            this.animationTimeMs += now - this.lastAnimationTimestampMs;
        }
        this.lastAnimationTimestampMs = now;
        return this.animationTimeMs / 1000;
    }

    private startAnimationInterval(): void {
        if (this.animationIntervalId !== null) return;
        this.animationIntervalId = setInterval(() => {
            if (!this.shouldRunIdleAnimation()) {
                this.stopAnimationInterval();
                return;
            }
            if (this.config.getEnabled()) this.map?.triggerRepaint();
        }, 50);
    }

    private stopAnimationInterval(): void {
        if (this.animationIntervalId === null) return;
        clearInterval(this.animationIntervalId);
        this.animationIntervalId = null;
    }

    private addFocusListeners(): void {
        if (typeof window !== 'undefined') {
            window.addEventListener('focus', this.handleFocusStateChange);
            window.addEventListener('blur', this.handleFocusStateChange);
            window.addEventListener('pageshow', this.handleFocusStateChange);
            window.addEventListener('pagehide', this.handleFocusStateChange);
        }
        if (typeof document !== 'undefined') {
            document.addEventListener('visibilitychange', this.handleFocusStateChange);
        }
    }

    private removeFocusListeners(): void {
        if (typeof window !== 'undefined') {
            window.removeEventListener('focus', this.handleFocusStateChange);
            window.removeEventListener('blur', this.handleFocusStateChange);
            window.removeEventListener('pageshow', this.handleFocusStateChange);
            window.removeEventListener('pagehide', this.handleFocusStateChange);
        }
        if (typeof document !== 'undefined') {
            document.removeEventListener('visibilitychange', this.handleFocusStateChange);
        }
    }

    private updateGrid(gl: WebGLRenderingContext | WebGL2RenderingContext, circles: Array<[number, number]>): void {
        if (!this.state) return;
        const s = this.state;

        if (circles.length === 0 && this.revealBuf) {
            this.revealBuf.fill(0);
            this.lastCircleCount = 0;
            this.bboxTxMin = Number.POSITIVE_INFINITY;
            this.bboxTxMax = Number.NEGATIVE_INFINITY;
            this.bboxTyMin = Number.POSITIVE_INFINITY;
            this.bboxTyMax = Number.NEGATIVE_INFINITY;
            s.gridDirty = true;
        }
        if (circles.length === 0) {
            s.gridW = 0;
            return;
        }

        const newStart = this.lastCircleCount;
        if (newStart >= circles.length) return;

        const rTiles = Math.ceil(VISUAL_REVEAL_RADIUS_METERS / TILE_SIZE_METERS);
        const feather = rTiles * 0.3;
        const outerR = rTiles + feather * 0.5;
        const MARGIN = Math.ceil(outerR) + 5;

        for (let i = newStart; i < circles.length; i++) {
            const entry = circles[i];
            if (!entry) continue;
            const tx = Math.floor(entry[0] / TILE_SIZE_METERS);
            const ty = Math.floor(entry[1] / TILE_SIZE_METERS);
            if (tx < this.bboxTxMin) this.bboxTxMin = tx;
            if (tx > this.bboxTxMax) this.bboxTxMax = tx;
            if (ty < this.bboxTyMin) this.bboxTyMin = ty;
            if (ty > this.bboxTyMax) this.bboxTyMax = ty;
        }
        const gMinTx = this.bboxTxMin - MARGIN;
        const gMinTy = this.bboxTyMin - MARGIN;
        const gW = Math.min(this.bboxTxMax - this.bboxTxMin + 1 + MARGIN * 2, s.maxTextureSize);
        const gH = Math.min(this.bboxTyMax - this.bboxTyMin + 1 + MARGIN * 2, s.maxTextureSize);

        const needsRealloc =
            s.gridW === 0 ||
            gMinTx < s.gridMinTx ||
            gMinTx + gW > s.gridMinTx + s.gridW ||
            gMinTy < s.gridMinTy ||
            gMinTy + gH > s.gridMinTy + s.gridH;

        if (needsRealloc) {
            gl.bindTexture(gl.TEXTURE_2D, s.gridTexture);
            gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
            if (isWebGL2(gl)) {
                gl.texImage2D(gl.TEXTURE_2D, 0, gl.R8, gW, gH, 0, gl.RED, gl.UNSIGNED_BYTE, null);
            } else {
                gl.texImage2D(gl.TEXTURE_2D, 0, gl.LUMINANCE, gW, gH, 0, gl.LUMINANCE, gl.UNSIGNED_BYTE, null);
            }
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
            s.gridMinTx = gMinTx;
            s.gridMinTy = gMinTy;
            s.gridW = gW;
            s.gridH = gH;
            const newBuf = new Uint8Array(gW * gH);
            if (this.revealBuf && this.revealW > 0) {
                const oxOff = this.revealMinTx - gMinTx;
                const oyOff = this.revealMinTy - gMinTy;
                for (let y = 0; y < this.revealH; y++) {
                    const dy = y + oyOff;
                    if (dy < 0 || dy >= gH) continue;
                    const srcStart = y * this.revealW;
                    const xSrcStart = Math.max(0, -oxOff);
                    const xSrcEnd = Math.min(this.revealW, gW - oxOff);
                    if (xSrcStart >= xSrcEnd) continue;
                    const dstStart = dy * gW + xSrcStart + oxOff;
                    newBuf.set(this.revealBuf.subarray(srcStart + xSrcStart, srcStart + xSrcEnd), dstStart);
                }
            }
            this.revealBuf = newBuf;
            this.revealMinTx = gMinTx;
            this.revealMinTy = gMinTy;
            this.revealW = gW;
            this.revealH = gH;
            s.gridDirty = true;
            s.dirtyRowMin = 0;
            s.dirtyRowMax = gH - 1;
        }

        const buf = this.revealBuf;
        if (!buf) return;
        // Compare d³ instead of d to avoid Math.cbrt for pixels outside or well inside the shape.
        const outerR3 = outerR * outerR * outerR;
        const innerR = rTiles - feather;
        const innerR3 = innerR > 0 ? innerR * innerR * innerR : 0;
        let dirtyRowMin = s.gridDirty ? s.dirtyRowMin : this.revealH;
        let dirtyRowMax = s.gridDirty ? s.dirtyRowMax : -1;

        for (let ci = newStart; ci < circles.length; ci++) {
            const entry = circles[ci];
            if (!entry) continue;
            const [mx, my] = entry;
            const centerTx = Math.floor(mx / TILE_SIZE_METERS);
            const centerTy = Math.floor(my / TILE_SIZE_METERS);
            const cx = centerTx - this.revealMinTx;
            const cy = centerTy - this.revealMinTy;
            if (cx >= 0 && cx < this.revealW && cy >= 0 && cy < this.revealH && buf[cy * this.revealW + cx] === 255)
                continue;
            const x0 = Math.max(0, cx - Math.ceil(outerR));
            const x1 = Math.min(this.revealW - 1, cx + Math.ceil(outerR));
            const y0 = Math.max(0, cy - Math.ceil(outerR));
            const y1 = Math.min(this.revealH - 1, cy + Math.ceil(outerR));
            for (let y = y0; y <= y1; y++) {
                const dy = y - cy;
                const ady = Math.abs(dy);
                const ady3 = ady * ady * ady;
                const row = y * this.revealW;
                let rowDirty = false;
                for (let x = x0; x <= x1; x++) {
                    if (buf[row + x] === 255) continue;
                    const dx = x - cx;
                    const adx = Math.abs(dx);
                    const d3 = adx * adx * adx + ady3;
                    if (d3 > outerR3) continue;
                    if (d3 <= innerR3) {
                        buf[row + x] = 255;
                        rowDirty = true;
                        continue;
                    }
                    const d = Math.cbrt(d3);
                    const nx = (x + this.revealMinTx) * 0.7 + (y + this.revealMinTy) * 1.3;
                    const noise =
                        (Math.sin(nx * 1.1) * 0.5 + Math.sin(nx * 2.7 + 3.1) * 0.3 + Math.sin(nx * 5.3 + 1.7) * 0.2) *
                        feather *
                        0.5;
                    const edgeDist = rTiles + noise - d;
                    if (edgeDist <= -feather) continue;
                    let t = Math.max(0, Math.min(1, edgeDist / feather));
                    t = t * t * (3 - 2 * t);
                    const val = Math.round(t * 255);
                    if (val > (buf[row + x] ?? 0)) {
                        buf[row + x] = val;
                        rowDirty = true;
                    }
                }
                if (rowDirty) {
                    if (y < dirtyRowMin) dirtyRowMin = y;
                    if (y > dirtyRowMax) dirtyRowMax = y;
                }
            }
        }
        this.lastCircleCount = circles.length;
        if (dirtyRowMax >= dirtyRowMin) {
            s.gridDirty = true;
            s.dirtyRowMin = dirtyRowMin;
            s.dirtyRowMax = dirtyRowMax;
        }
        if (!s.gridDirty) return;

        const uploadY = s.dirtyRowMin;
        const uploadH = s.dirtyRowMax - s.dirtyRowMin + 1;
        const uploadOffset = uploadY * s.gridW;
        const uploadData = buf.subarray(uploadOffset, uploadOffset + uploadH * s.gridW);
        gl.bindTexture(gl.TEXTURE_2D, s.gridTexture);
        gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
        if (isWebGL2(gl)) {
            gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, uploadY, s.gridW, uploadH, gl.RED, gl.UNSIGNED_BYTE, uploadData);
        } else {
            gl.texSubImage2D(
                gl.TEXTURE_2D,
                0,
                0,
                uploadY,
                s.gridW,
                uploadH,
                gl.LUMINANCE,
                gl.UNSIGNED_BYTE,
                uploadData,
            );
        }
        s.gridDirty = false;
    }
}

function compileProgram(
    gl: WebGLRenderingContext | WebGL2RenderingContext,
    vertSrc: string,
    fragSrc: string,
): WebGLProgram {
    const vert = compileShader(gl, gl.VERTEX_SHADER, vertSrc);
    const frag = compileShader(gl, gl.FRAGMENT_SHADER, fragSrc);
    const program = gl.createProgram();
    if (!program) throw new Error('FogOfWarLayer: createProgram null');
    gl.attachShader(program, vert);
    gl.attachShader(program, frag);
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
        const err = gl.getProgramInfoLog(program);
        gl.deleteProgram(program);
        throw new Error(`FogOfWarLayer link failed: ${err}`);
    }
    return program;
}

function compileShader(gl: WebGLRenderingContext | WebGL2RenderingContext, type: number, src: string): WebGLShader {
    const shader = gl.createShader(type);
    if (!shader) throw new Error('FogOfWarLayer: createShader null');
    gl.shaderSource(shader, src);
    gl.compileShader(shader);
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
        const err = gl.getShaderInfoLog(shader);
        gl.deleteShader(shader);
        throw new Error(`FogOfWarLayer compile failed: ${err}`);
    }
    return shader;
}
